/* eslint-disable camelcase */

import React from 'react';
import {
  Badge, Stack, Text, useTranslation, formatDate,
} from '@piwikpro/ui-components';
import { BillingCycle, ScheduledChangeAction, SubscriptionStatus } from '@piwikpro/marketing-crate/redux';

import styles from './SubscriptionBadge.module.css';

const formatUtcCalendarDate = (
  isoDate: string,
  mode: 'administration' | 'support-panel',
): string => {
  const dateLabel = formatDate(new Date(isoDate), { format: 'LL', timeZone: 'UTC' });

  return mode === 'support-panel' ? dateLabel : `${dateLabel} (UTC)`;
};

const SubscriptionBadge = ({
  subscription,
  mode = 'administration',
}: {
  subscription: {
    status: SubscriptionStatus
    next_payment: string | null
    billing_cycle: BillingCycle | null
    scheduled_change_action: ScheduledChangeAction
    scheduled_change_effective_at: string | null
  }
  mode?: 'administration' | 'support-panel'
}) => {
  const { t } = useTranslation();

  const hasScheduledChange = Boolean(subscription.scheduled_change_action);

  const text = (() => {
    if (subscription.scheduled_change_action === 'cancel') {
      return t('paddle.payment.current-subscription.badge.cancelled');
    }
    if (subscription.billing_cycle === 'month') {
      return t('paddle.payment.current-subscription.billing-cycle.monthly');
    }
    if (subscription.billing_cycle === 'year') {
      return t('paddle.payment.current-subscription.billing-cycle.yearly');
    }

    throw new Error('Invalid info provided for the subscription badge');
  })();

  const captionDate = hasScheduledChange
    ? subscription.scheduled_change_effective_at
    : subscription.next_payment;
  const showActiveUntil = hasScheduledChange || !subscription.next_payment;

  return (
    <Stack verticalAlignment="center" spacing="narrow">
      <Stack.Item>
        <Badge
          color="white"
          iconLeft={{
            size: 'small',
            name: hasScheduledChange ? 'refresh-error' : 'refresh',
            color: hasScheduledChange ? 'gray' : 'green',
          }}
          text={text}
          size="medium"
        />
      </Stack.Item>
      <Stack.Item>
        <Text as="p" size={12} color="InkLighter" dataId="Caption">
          {showActiveUntil
            ? (
              <>
                <span>{`${t('platform.license.plan.active-until')} `}</span>
                <span>
                  {captionDate ? formatUtcCalendarDate(captionDate, mode) : null}
                </span>
              </>
            )
            : (
              <>
                <span>{`${t('paddle.payment.current-subscription.next-payment')} `}</span>
                <span className={subscription.status === 'past_due' ? styles.red : null}>
                  {captionDate ? formatUtcCalendarDate(captionDate, mode) : null}
                </span>
              </>
            )}
        </Text>
      </Stack.Item>
    </Stack>
  );
};

export { SubscriptionBadge };
